import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { Upload as UploadIcon, Add as AddIcon } from '@mui/icons-material';
import { gamoraApi, EmbedResponse } from '../services/api';
import axios from 'axios';
import {
  adminCard,
  adminPageTitle,
  adminPageSubtitle,
  adminSectionTitle,
  adminPrimaryButton,
  adminSecondaryButton,
  adminInput,
  adminAlert,
} from './adminStyles';

const EmbeddingUpload: React.FC = () => {
  const [urls, setUrls] = useState<string[]>(['']);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EmbedResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flushLoading, setFlushLoading] = useState(false);
  const [flushResult, setFlushResult] = useState<string | null>(null);

  const handleUrlChange = (index: number, value: string) => {
    const newUrls = [...urls];
    newUrls[index] = value;
    setUrls(newUrls);
  };

  const addUrl = () => {
    setUrls([...urls, '']);
  };

  const removeUrl = (index: number) => {
    if (urls.length > 1) {
      const newUrls = urls.filter((_, i) => i !== index);
      setUrls(newUrls);
    }
  };

  const handleEmbed = async () => {
    const validUrls = urls.filter(url => url.trim() !== '');
    
    if (validUrls.length === 0) {
      setError('Please enter at least one URL');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await gamoraApi.embed(validUrls);
      // Check if backend returned an error status
      if (response.status === 'error') {
        setError(response.message || response.error || 'Failed to embed documents. Please try again.');
        setResult(null);
      } else {
        setResult(response);
      }
    } catch (err) {
      setError('Failed to embed documents. Please try again.');
      console.error('Embed error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmbedDefault = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await gamoraApi.embed(); // Use default URLs
      // Check if backend returned an error status
      if (response.status === 'error') {
        setError(response.message || response.error || 'Failed to embed default documents. Please try again.');
        setResult(null);
      } else {
        setResult(response);
      }
    } catch (err) {
      setError('Failed to embed default documents. Please try again.');
      console.error('Embed error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFlush = async () => {
    setFlushLoading(true);
    setFlushResult(null);
    setError(null);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      const response = await axios.post(`${API_BASE_URL}/collection/flush`);
      setFlushResult(response.data.message || 'Flushed successfully.');
    } catch (err: any) {
      setFlushResult('Failed to flush data.');
      setError(err?.message || 'Failed to flush data.');
    } finally {
      setFlushLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography component="h1" align="center" sx={{ ...adminPageTitle, fontSize: '1.1rem', mb: 0.5 }}>
        Document Embedding
      </Typography>
      
      <Typography align="center" sx={{ ...adminPageSubtitle, mb: 4, fontSize: '0.82rem', color: '#6b7280' }}>
        Upload and embed documents from URLs to build the knowledge base
      </Typography>

      {/* URL Input */}
      <Box sx={{ ...adminCard, p: 2.5, mb: 3 }}>
        <Typography sx={{ ...adminSectionTitle, mb: 2 }}>
          Enter URLs to embed:
        </Typography>
        
        {urls.map((url, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              label={`URL ${index + 1}`}
              value={url}
              onChange={(e) => handleUrlChange(index, e.target.value)}
              placeholder="https://example.com/document"
              variant="outlined"
              disabled={loading}
              sx={adminInput}
            />
            {urls.length > 1 && (
              <Button
                variant="outlined"
                onClick={() => removeUrl(index)}
                disabled={loading}
                sx={{
                  minWidth: 40,
                  borderRadius: '10px',
                  borderColor: '#e5e7eb',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  color: '#6b7280',
                  '&:hover': { borderColor: '#d1d5db', bgcolor: '#f3f4f6', color: '#374151' },
                }}
              >
                ×
              </Button>
            )}
          </Box>
        ))}
        
        <Button
          variant="outlined"
          onClick={addUrl}
          disabled={loading}
          startIcon={<AddIcon />}
          sx={{ mt: 1, ...adminSecondaryButton, border: '1px solid #e5e7eb' }}
        >
          Add URL
        </Button>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          onClick={handleEmbed}
          disabled={loading || urls.every(url => !url.trim())}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
          sx={{ minWidth: 150, ...adminPrimaryButton }}
        >
          {loading ? 'Embedding...' : 'Embed URLs'}
        </Button>
        
        <Button
          variant="outlined"
          onClick={handleEmbedDefault}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
          sx={{
            minWidth: 150,
            ...adminSecondaryButton,
            border: '1px solid #e5e7eb',
            '&:hover': { borderColor: '#d1d5db', bgcolor: '#f3f4f6' },
          }}
        >
          {loading ? 'Embedding...' : 'Embed Default'}
        </Button>

        <Button
          variant="outlined"
          onClick={handleEmbedDefault}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          sx={{
            minWidth: 180,
            ...adminSecondaryButton,
            border: '1px solid #e5e7eb',
            '&:hover': { borderColor: '#d1d5db', bgcolor: '#f3f4f6' },
          }}
        >
          {loading ? 'Embedding...' : 'Embed All Collections'}
        </Button>

        <Button
          variant="outlined"
          onClick={handleFlush}
          disabled={flushLoading || loading}
          startIcon={flushLoading ? <CircularProgress size={20} /> : <UploadIcon />}
          sx={{
            minWidth: 150,
            ...adminSecondaryButton,
            border: '1px solid #e5e7eb',
            '&:hover': { borderColor: '#d1d5db', bgcolor: '#f3f4f6' },
          }}
        >
          {flushLoading ? 'Flushing...' : 'Flush Data'}
        </Button>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, ...adminAlert }}>
          {error}
        </Alert>
      )}

      {/* Results Display */}
      {result && (
        <Card sx={{ ...adminCard, boxShadow: 'none' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 650, color: '#111827', letterSpacing: '-0.02em', mb: 1 }}>
              Embedding complete
            </Typography>
            <Divider sx={{ my: 2, borderColor: '#eceef1' }} />
            <List disablePadding>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemText
                  primary="Status"
                  secondary={result.status}
                  primaryTypographyProps={{ sx: { fontSize: '0.78rem', fontWeight: 500, color: '#6b7280' } }}
                  secondaryTypographyProps={{ sx: { fontSize: '0.82rem', color: '#374151' } }}
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemText
                  primary="Pages Processed"
                  secondary={result.pages_processed}
                  primaryTypographyProps={{ sx: { fontSize: '0.78rem', fontWeight: 500, color: '#6b7280' } }}
                  secondaryTypographyProps={{ sx: { fontSize: '0.82rem', color: '#374151' } }}
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemText
                  primary="Total Chunks"
                  secondary={result.total_chunks_processed}
                  primaryTypographyProps={{ sx: { fontSize: '0.78rem', fontWeight: 500, color: '#6b7280' } }}
                  secondaryTypographyProps={{ sx: { fontSize: '0.82rem', color: '#374151' } }}
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemText
                  primary="Collection Count"
                  secondary={result.final_collection_count}
                  primaryTypographyProps={{ sx: { fontSize: '0.78rem', fontWeight: 500, color: '#6b7280' } }}
                  secondaryTypographyProps={{ sx: { fontSize: '0.82rem', color: '#374151' } }}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      )}
      {flushResult && (
        <Alert severity={flushResult.includes('success') ? 'success' : 'error'} sx={{ mt: 3, ...adminAlert }}>
          {flushResult}
        </Alert>
      )}
    </Box>
  );
};

export default EmbeddingUpload; 